import Colors from '../../constants/Colors';
import { DateSlots } from '../../contexts/checkout';
import useColorScheme from '../../hooks/useColorScheme';
import { dateStringToNumeric } from '../../utils/date';
import { formatDateHoursRange } from '../../utils/locations';
import { formatWeekDates, WeekEntry } from '../../utils/weekly';
import { Icon } from '../Icon';
import Row, { TransparentRow } from '../Row';
import { TextBodySmall } from '../StyledText';

export function WeeklyDatesPreview({
  weeks,
  weekEntries,
  dateSlotsPerWeek = {},
}: {
  weeks: Record<string, string[]> | undefined;
  weekEntries: WeekEntry[] | undefined;
  dateSlotsPerWeek: Record<string, DateSlots> | undefined;
}) {
  const theme = useColorScheme();
  if (!weeks || !weekEntries) {
    return null;
  }
  const weekEntriesById = weekEntries.reduce(
    (acc, cur) => {
      return {
        ...acc,
        [cur.id]: cur,
      };
    },
    {} as Record<string, WeekEntry>
  );
  return (
    <>
      {Object.keys(weeks)
        .sort()
        .map((weekId) => {
          const slots = dateSlotsPerWeek[weekId] || null;
          if (!slots) {
            return null;
          }

          const formattedDays = formatWeekDates(slots);

          const firstDay = Object.values(slots)[0] || {};
          const hours = Object.entries(firstDay)
            .filter(([daytime]) => (weeks[weekId] || []).includes(daytime))
            .map(([, bookingSlot]) => [bookingSlot.dropoff, bookingSlot.pickup])
            .flat()
            .sort();

          const firstHour = hours[0];
          const lastHour = hours[hours.length - 1];

          const formattedHours =
            !firstHour || !lastHour
              ? ''
              : formatDateHoursRange(firstHour, lastHour);

          const weekEntry = weekEntriesById[weekId];
          const blackoutDays = weekEntry
            ? getBlackoutDays(weekEntry, dateSlotsPerWeek)
            : [];

          const filteredBlackoutDays =
            blackoutDays.length === 1
              ? blackoutDays.filter((d) => d.idx !== 0 && d.idx !== 4)
              : blackoutDays.length === 4
                ? [] // we have only one day selected so no need with displaying blackout days
                : blackoutDays;

          const blackoutDaysFormatted = formatBlackoutDays(
            filteredBlackoutDays.map((d) => d.entry)
          );
          return (
            <TransparentRow
              key={weekId}
              style={{
                marginBottom: 5,
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                columnGap: 16,
              }}
            >
              <TextBodySmall style={{ color: Colors[theme].textSecondary }}>
                {formattedDays}, {formattedHours}
              </TextBodySmall>
              {filteredBlackoutDays.length > 0 ? (
                <Row
                  style={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  <Icon
                    name="dateBlackout"
                    style={{
                      width: 20,
                      height: 20,
                      marginRight: 5,
                    }}
                    color={Colors[theme].error}
                  />
                  <TextBodySmall>
                    Blackout Date(s): {blackoutDaysFormatted}
                  </TextBodySmall>
                </Row>
              ) : null}
            </TransparentRow>
          );
        })}
    </>
  );
}

function getBlackoutDays(
  weekEntry: WeekEntry,
  dateSlotsPerWeek: Record<string, DateSlots>
) {
  const blackoutDays =
    weekEntry.entries
      .map((e, idx) => ({ entry: e, idx }))
      .filter(
        (d) =>
          !Object.keys(dateSlotsPerWeek[weekEntry.id] || {}).includes(d.entry)
      ) || [];

  return blackoutDays;
}
function formatBlackoutDays(dateStrings: string[]) {
  const datesGrouped = dateStrings
    .map((ds) => {
      const [year, month, day] = ds.split('-');
      if (!year || !month || !day) {
        return null;
      }
      return [`${year}-${month}`, day] as const;
    })
    .reduce(
      (acc, cur) => {
        if (cur === null) {
          return acc;
        }
        const [month, day] = cur;
        acc[month] ||= [];
        acc[month]?.push(day);
        return acc;
      },
      {} as Record<string, string[]>
    );
  return Object.entries(datesGrouped)
    .map(([month, days]) => {
      return `${dateStringToNumeric(month, { month: 'short' })} ${days
        .map((d) => parseInt(d, 10))
        .join(', ')}`;
    })
    .join(', ');
}
